import React, { useMemo } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import './RichNoteEditor.css';
import { isEditorContentEmpty } from '../core/utils/noteRichText';

/**
 * Rich text editor for team notes. Value is HTML (Quill / Snow theme).
 *
 * Quill fires non-user onChange on mount (api/silent). Under StrictMode those
 * can wipe seeded edit HTML with empty markup — block only that wipe case.
 * Still sync empty↔empty so a new note editor stays aligned and typing works.
 */
const RichNoteEditor = ({
  id,
  value,
  onChange,
  placeholder = '',
  className = '',
}) => {
  const modules = useMemo(
    () => ({
      toolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['blockquote'],
        ['clean'],
      ],
    }),
    []
  );

  const formats = useMemo(
    () => [
      'bold',
      'italic',
      'underline',
      'strike',
      'list',
      'indent',
      'blockquote',
    ],
    []
  );

  return (
    <div className={`rich-note-editor ${className}`.trim()}>
      <label className="sr-only" htmlFor={id}>
        Note content
      </label>
      <ReactQuill
        id={id}
        theme="snow"
        value={value ?? ''}
        onChange={(content, _delta, source) => {
          if (!onChange) return;
          if (source === 'user') {
            onChange(content);
            return;
          }
          // Block programmatic wipes of existing content (edit open / StrictMode).
          if (isEditorContentEmpty(content) && !isEditorContentEmpty(value)) {
            return;
          }
          if (content === value) return;
          onChange(content);
        }}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
      />
    </div>
  );
};

export default RichNoteEditor;
